import axios from 'axios';
import TokenManager from '../utils/tokenManager';

// These instances deliberately bypass the legacy token interceptor in api/index.js.
const orderClient = axios.create({ timeout: 100000, validateStatus: () => true });
const refreshClient = axios.create({ timeout: 15000 });
let refreshTask = null;

export const captureAuthSession = () => ({
  accessToken: TokenManager.getAccessToken(),
  refreshToken: TokenManager.getRefreshToken(),
  userInfo: localStorage.getItem('userInfo'),
  uid: localStorage.getItem('uid'),
});

export const isCurrentAuthSession = (session, compareAccessToken = false) => {
  const current = captureAuthSession();
  return current.refreshToken === session.refreshToken &&
    current.userInfo === session.userInfo && current.uid === session.uid &&
    (!compareAccessToken || current.accessToken === session.accessToken);
};

const authError = code => Object.assign(new Error(code), { code });
const assertSession = (session, compareAccessToken = false) => {
  if (!isCurrentAuthSession(session, compareAccessToken)) {
    throw authError('AUTH_SESSION_CHANGED');
  }
};
const nonBlank = value => typeof value === 'string' && value.trim().length > 0;
const invalidIdentity = body => body?.data?.access_token_invalid === true ||
  ['NO_TOKEN', 'TOKEN_INVALID', 'USER_KEY_MISMATCH'].includes(body?.data?.auth_status);
const expired = (status, body) =>
  body?.code !== 'ORDER_QUERY_LOCKED' && (
  (status === 200 && body?.data?.can_refresh === true) ||
  (status === 401 && (body?.code === 'AXTORA_ACCESS_TOKEN_EXPIRED' ||
    (body?.data?.reason === 'access_token_expired' && body?.data?.can_refresh === true))));

const refreshAccessToken = session => {
  if (refreshTask && refreshTask.session.accessToken === session.accessToken &&
    isCurrentAuthSession(refreshTask.session)) {
    return refreshTask.promise;
  }

  const task = { session };
  // Defer execution so even a missing RT shares the same failure among concurrent callers.
  task.promise = Promise.resolve().then(async () => {
    try {
      assertSession(session, true);
      if (!nonBlank(session.refreshToken)) throw authError('AUTH_REFRESH_FAILED');
      const response = await refreshClient.post(
        '/testapi/user/stander_login_controller/refresh_token',
        { refresh_token: session.refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      );
      assertSession(session, true);
      const body = response.data;
      if (response.status !== 200 || body?.code !== 1 || !nonBlank(body?.tokens?.access_token)) {
        throw authError('AUTH_REFRESH_FAILED');
      }
      TokenManager.setAccessToken(body.tokens.access_token);
      return body.tokens.access_token;
    } catch (error) {
      assertSession(session, true);
      window.dispatchEvent(new CustomEvent('auth:token-refresh-failed', { detail: session }));
      throw authError('AUTH_REFRESH_FAILED');
    }
  }).finally(() => {
    if (refreshTask === task) refreshTask = null;
  });
  refreshTask = task;
  return task.promise;
};

// Returns the JSON body, matching the existing order API's response shape.
export const authenticatedPost = async (url, data, { onResponse } = {}) => {
  const session = captureAuthSession();
  const requestBody = { ...data };
  let accessToken = session.accessToken;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    assertSession(session);
    let response;
    try {
      response = await orderClient.post(url, requestBody, {
        headers: {
          'Content-Type': 'application/json',
          ...(nonBlank(accessToken) ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });
    } catch (error) {
      assertSession(session);
      throw error;
    }
    assertSession(session);
    const { status, data: body } = response;

    // An old AT's response must not log out an account that has just been refreshed.
    if (status === 200 && invalidIdentity(body)) {
      assertSession({ ...session, accessToken }, true);
      window.dispatchEvent(new CustomEvent('auth:session-expired', {
        detail: { ...session, accessToken },
      }));
      throw authError('AUTH_SESSION_EXPIRED');
    }

    if (status === 200) onResponse?.(body);

    if (expired(status, body) && attempt === 0) {
      const latestToken = TokenManager.getAccessToken();
      accessToken = nonBlank(latestToken) && latestToken !== accessToken
        ? latestToken
        : await refreshAccessToken({ ...session, accessToken });
      assertSession(session);
      continue;
    }

    if (status < 200 || status >= 300) {
      // Keep the HTTP response for business error handling, without logging credentials.
      throw Object.assign(new Error(`HTTP ${status}`), { response: { status, data: body } });
    }
    if (status === 200 && expired(status, body)) throw authError('AUTH_REPLAY_EXPIRED');
    return body;
  }
};
